import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

// User ID Validation and Generation Utilities
const validateUserId = (userId) => {
  const validationRegex = /^(U-\d+(-[a-z0-9]+)?|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/;
  return validationRegex.test(userId);
};

const generateUserId = () => {
  return `U-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
};

const australianStates = [
  { value: 'NSW', label: 'New South Wales' },
  { value: 'VIC', label: 'Victoria' },
  { value: 'QLD', label: 'Queensland' },
  { value: 'WA', label: 'Western Australia' },
  { value: 'SA', label: 'South Australia' },
  { value: 'TAS', label: 'Tasmania' },
  { value: 'ACT', label: 'Australian Capital Territory' },
  { value: 'NT', label: 'Northern Territory' }
];

// Thank You Popup Component
const ThankYouPopup = ({ onClose, membershipType }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-lg shadow-xl text-center max-w-md w-full">
        <h2 className="text-2xl font-bold mb-4 text-green-600">Thank You!</h2>
        <p className="mb-6 text-gray-700">
          Welcome to the {membershipType} Family! 
          We're excited to have you on board.
        </p>
        <button 
          onClick={onClose}
          className="bg-orange-500 text-white py-2 px-6 rounded-md hover:bg-orange-600 transition"
        >
          Continue
        </button>
      </div>
    </div>
  );
};

// Submission Blocked Popup Component
const SubmissionBlockedPopup = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-lg shadow-xl text-center max-w-md w-full">
        <div className="mb-4 text-red-600">
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className="h-16 w-16 mx-auto" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
            />
          </svg>
        </div>
        <h2 className="text-2xl font-bold mb-4 text-red-600">Submission Not Allowed</h2>
        <p className="mb-6 text-gray-700">
          A membership request has already been submitted with this email address.
        </p>
        <button 
          onClick={onClose}
          className="bg-red-500 text-white py-2 px-6 rounded-md hover:bg-red-600 transition"
        >
          Understood
        </button>
      </div>
    </div>
  );
};

// Main Billing Form Component
const BillingForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showThankYouPopup, setShowThankYouPopup] = useState(false);
  const [submissionBlocked, setSubmissionBlocked] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [userId, setUserId] = useState('');
  const [phone, setPhone] = useState('');

  // Initialize formData with empty values
  const [formData, setFormData] = useState({
    userId: '',
    name: '',
    email: '',
    phoneNumber: '',
    address: '',
    from: '',
    firstName: '',
    lastName: '',
    addressLine1: '',
    suburb: '',
    stateProvince: '',
    postalCode: '',
    profession: '',
    dateOfBirth: '',
    gender: '',
    referredBy: '',
    maritalStatus: ''
  });

  const [agreedToTerms, setAgreedToTerms] = useState(true);
  const [passport, setPassport] = useState(null);
  const [supportingDocuments, setSupportingDocuments] = useState([]);

  // Function to get user email from localStorage
  const getUserEmail = () => {
    const userValue = localStorage.getItem('user');
    if (userValue) {
      try {
        const userData = JSON.parse(userValue);
        return userData.value?.email || '';
      } catch (error) {
        console.error('Error parsing user data:', error);
        return '';
      }
    }
    return '';
  };

  useEffect(() => {
    const checkLoginStatus = () => {
      const userValue = localStorage.getItem('user');
      const generatedUserId = generateUserId();
      setUserId(generatedUserId);

      if (userValue) {
        try {
          const userData = JSON.parse(userValue);
          const userDetails = userData.value;
          setIsLoggedIn(true);
          
          setFormData(prevData => ({
            ...prevData,
            userId: generatedUserId,
            firstName: userDetails.firstName || '',
            lastName: userDetails.lastName || '',
            email: userDetails.email || '',
            phoneNumber: userDetails.phone || '',
            maritalStatus: userDetails.maritalStatus || '',
            gender: userDetails.gender || '',
            dateOfBirth: userDetails.dateOfBirth || '',
            profession: userDetails.profession || '',
            addressLine1: userDetails.addressLine1 || '',
            suburb: userDetails.suburb || '',
            stateProvince: userDetails.stateProvince || '',
            postalCode: userDetails.postalCode || ''
          }));
          
          if (userDetails.phone) {
            setPhone(userDetails.phone);
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      } else {
        setFormData(prevData => ({
          ...prevData,
          userId: generatedUserId
        }));
      }
    };

    checkLoginStatus();

    if (location.state && location.state.selectedMembership) {
      setSelectedMembership(location.state.selectedMembership);
    }
  }, [location]);

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    
    if (name === 'passport') {
      setPassport(files[0]);
      return;
    }

    if (name === 'supportingDocuments') {
      setSupportingDocuments(Array.from(files));
      return;
    }

    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handlePhoneChange = (value, country) => {
    setPhone(value);
    setFormData(prevData => ({
      ...prevData,
      phoneNumber: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isLoggedIn) {
      console.error('Login Error: User not logged in');
      setError('Please log in before proceeding');
      return;
    }  
    const requiredFields = [
      'firstName', 'lastName', 'email', 'phoneNumber', 
      'addressLine1', 'suburb', 'stateProvince', 'postalCode', 
      'profession', 'dateOfBirth', 'gender', 'maritalStatus'
    ];
    const missingFields = requiredFields.filter(field => !formData[field]);
    if (missingFields.length > 0) {
      console.error('Validation Error: Missing fields', missingFields);
      setError(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }
    
    const membershipData = {
      userId: userId,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: phone,
      address: `${formData.addressLine1}, ${formData.suburb}, ${formData.stateProvince}, ${formData.postalCode}`,
      username: formData.email.split('@')[0], // Extract username from email
      profession: formData.profession,
      dateOfBirth: formData.dateOfBirth,
      gender: formData.gender,
      referredBy: formData.referredBy || '',
      maritalStatus: formData.maritalStatus,
      from: formData.profession,
      membershipName: selectedMembership.name,
      passport: passport ? passport.name : null,
      supportingDocuments: supportingDocuments.map(doc => doc.name)
    };
    setLoading(true);
    setError('');

    try {
        const payload = {
            productName: selectedMembership.name,
            price: selectedMembership.amt,
            customerEmail: formData.email,
        };
        const response = await fetch("https://atabackend.quindl.com/create-checkout-session", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            throw new Error("Failed to create checkout session");
        }

        const data = await response.json();
        await saveToLocalStorage(membershipData);
        setTimeout(() => {
            window.location.href = data.url;
        }, 1000);

      } catch (error) {
          console.error("Checkout failed:", error);
          alert("Something went wrong. Please try again.");
          return
      }
    };

    const saveToLocalStorage = async (membershipData) => {
      try {
        localStorage.setItem('membershipData', JSON.stringify(membershipData));
      } catch (err) {
        console.error('Full submission error:', err);
        setError(err.message || 'Failed to submit membership request');
      } finally {
        setLoading(false);
      }
    }
    
    const handleCheckboxChange = (e) => {
      setIsChecked(e.target.checked);
    };

    const handleTermsClick = (e) => {
      e.preventDefault();
      // Navigate in same window
      window.location.href = '/terms&conditions';
    };

    const handlePopupClose = () => {
      setShowThankYouPopup(false);

      // Ensure navigation happens only for Silver-Family and Gold-Family
      const allowedMemberships = ['Silver-Family', 'Gold-Family'];
      if (allowedMemberships.includes(selectedMembership?.name)) {
        navigate('/confirmation', {
          state: {
            membershipType: selectedMembership?.name,
            userId: userId
          }
        });
      } else {
        navigate('/')
      }
    };

    const handleLogin = () => {
      navigate('/login', { 
        state: { 
          from: location.pathname,
          membership: selectedMembership 
        } 
      });
    };

    return (
      <>
        {showThankYouPopup && (
          <ThankYouPopup 
            onClose={handlePopupClose} 
            membershipType={selectedMembership?.name || 'Silver/Gold'}
          />
        )}

        {submissionBlocked && (
          <SubmissionBlockedPopup 
            onClose={() => setSubmissionBlocked(false)} 
          />
        )}

        <form onSubmit={handleSubmit} className="bg-gradient-to-br from-purple-100 via-pink-50 to-blue-50 p-12">
          <div className="max-w-6xl mx-auto">
            <h1 className="text-2xl font-bold mb-8 text-gray-800">Primary Member Details</h1>
            
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
                {error}
              </div>
            )}

            <div className="lg:col-span-2 p-6">
              <div className="bg-white rounded-lg shadow-lg p-12 space-y-6">
                {/* Personal Information Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      className="w-full p-2 border border-gray-300 rounded-md"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      className="w-full p-2 border border-gray-300 rounded-md"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                {/* Contact Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      className="w-full p-2 border border-gray-300 rounded-md cursor-not-allowed"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      disabled={isLoggedIn}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <div className='border border-gray-300 rounded-md'>  
                    <PhoneInput
                      country={'au'} // Default country (Australia)
                      value={phone}
                      onChange={handlePhoneChange}
                      inputProps={{
                        name: 'phoneNumber',
                        required: true,
                        className: 'w-full p-2  px-12'
                      }}
                      containerClass="w-full"
                      inputClass="w-full p-2 border border-gray-300 rounded-md"
                      buttonClass="border border-gray-300 rounded-l-md"
                      dropdownClass="bg-white border border-gray-300"
                    />
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address line 1 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="addressLine1"
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={formData.addressLine1}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                {/* Location Details */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Suburb <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="suburb"
                      className="w-full p-2 border border-gray-300 rounded-md"
                      value={formData.suburb}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State/Province <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="stateProvince"
                      className="w-full p-2 border border-gray-300 rounded-md"
                      value={formData.stateProvince}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select your state</option>
                      {australianStates.map((state) => (
                        <option key={state.value} value={state.value}>
                          {state.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Postal Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="postalCode"
                      className="w-full p-2 border border-gray-300 rounded-md"
                      value={formData.postalCode}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                {/* Additional Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Profession <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="profession"
                      className="w-full p-2 border border-gray-300 rounded-md"
                      value={formData.profession}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date of Birth <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      className="w-full p-2 border border-gray-300 rounded-md"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Gender <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="gender"
                      className="w-full p-2 border border-gray-300 rounded-md"
                      value={formData.gender}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select your gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Referred by (optional)
                    </label>
                    <input
                      type="text"
                      name="referredBy"
                      className="w-full p-2 border border-gray-300 rounded-md"
                      value={formData.referredBy}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Marital Status <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="maritalStatus"
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={formData.maritalStatus}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select your Marital status</option>
                    <option value="single">Single</option>
                    <option value="married">Married</option>
                    <option value="divorced">Divorced</option>
                    <option value="widowed">Widowed</option>
                    <option value="separated">Separated</option>
                    <option value="domestic_partnership">Domestic Partnership</option>
                    <option value="civil_union">Civil Union</option>
                  </select>
                </div>
                 
              </div>
            </div>

            {/* Order Summary Section */}
            <div className='flex justify-center mt-6'>
              <div className="max-w-2xl lg:col-span-1">
                <div className="bg-white rounded-lg shadow-lg">
                  <div className="bg-gradient-to-r from-orange-500 to-red-600 p-4 rounded-t-lg">
                    <h2 className="text-xl font-bold text-white">Your Order</h2>
                  </div>
                  
                  <div className="p-6 space-y-6">
                    {selectedMembership && (
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{selectedMembership.name} x 1</span>
                        <span>{selectedMembership.price}</span>
                      </div>
                    )}

                    <div className="border-t pt-4">
                      <div className="flex justify-between items-center font-bold">
                        <span>TOTAL</span>
                        <span>{selectedMembership ? selectedMembership.price : '$ 0.00'}</span>
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <p className="text-sm text-gray-600 mb-4">
                        By purchasing the Membership / Event Tickets, you are agreed to ATA privacy policy.
                      </p>
                     <div className="flex items-start gap-2 max-w-lg">
                        <input
                          type="checkbox"
                          id="terms"
                          checked={isChecked}
                          onChange={handleCheckboxChange}
                          className="mt-1"
                        />
                        <label htmlFor="terms" className="text-sm text-gray-600">
                          I have read and agree to the website{' '}
                          <a
                            href="/tamilschools"
                            onClick={handleTermsClick}
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            terms and conditions
                          </a>
                          <span className="text-red-500 ml-1">*</span>
                        </label>
                      </div>
                    </div>

                    <button 
                      type="submit"
                      disabled={!isChecked || loading}
                      className={`w-full py-3 px-4 rounded-md font-bold transition duration-200 
                        ${!isChecked || loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-orange-500 hover:bg-orange-600 text-white'}`}
                    >
                      {loading ? 'SUBMITTING...' : 'PAY NOW'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </>
    );
};

export default BillingForm;